import React, { useEffect, useState } from "react";
import { Fund } from "@/types/Fund";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  useFundFormModalController,
  FormValues,
} from "./useFundFormModalController";
import { getFundCalendars } from "@/api/FundCalendar";
import { FundCalendar } from "@/types/FundCalendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface FundFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: FormValues) => Promise<void>;
  fund?: Fund;
  title: string;
  description: string;
}

export const FundFormModal: React.FC<FundFormModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  fund,
  title,
  description,
}) => {
  const controller = useFundFormModalController({
    open,
    onOpenChange,
    onSubmit,
    fund,
  });

  const [calendars, setCalendars] = useState<FundCalendar[]>([]);

  useEffect(() => {
    getFundCalendars().then(setCalendars);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Form {...controller.form}>
          <form
            onSubmit={controller.form.handleSubmit(controller.handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={controller.form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fund Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter fund name" {...field} required />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={controller.form.control}
              name="fundKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fund Key</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter fund key" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={controller.form.control}
              name="fundCalendarId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fund Calendar *</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value ? String(field.value) : ""}
                      onValueChange={(val) =>
                        field.onChange(val ? Number(val) : undefined)
                      }
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a calendar" />
                      </SelectTrigger>
                      <SelectContent>
                        {calendars.map((calendar) => (
                          <SelectItem
                            key={calendar.fundCalendarId}
                            value={String(calendar.fundCalendarId)}
                          >
                            {calendar.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
